import React, { useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import { lazy } from 'react-loosely-lazy';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, { ModalBody } from '@atlaskit/modal-dialog';
import Spinner from '@atlaskit/spinner';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import Placeholder from '@atlassian/jira-placeholder';
import type { Props } from './types';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyReleaseNotesModalContent = lazy(() =>
	import(/* webpackChunkName: "async-release-notes-modal" */ './index').then(
		(module) => module.ReleaseNotesModalContent,
	),
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyEmbeddedPageReleaseNotesEditorModal = lazy(() =>
	import(
		/* webpackChunkName: "async-embedded-page-release-notes-editor-modal" */ './embedded-page-release-notes-editor-modal'
	).then((module) => module.EmbeddedPageReleaseNotesEditorModal),
);

const LoadingModalBody = () => (
	<ModalBody>
		<LoadingWrapper>
			<Spinner size="large" />
		</LoadingWrapper>
	</ModalBody>
);

export const ReleaseNotesModal = (props: Props) => {
	const [confluenceReleaseNotesDraftUrl, setConfluenceReleaseNotesDraftUrl] = useState<
		string | null
	>(null);

	const isAutomatedReleaseNotesEnabled = fg('embedded_automated_release_notes');

	const { onClose } = props;

	const onCreateConfluenceReleaseNotes = useCallback((draftUrl: string) => {
		setConfluenceReleaseNotesDraftUrl(draftUrl);
	}, []);

	const onCloseConfluenceReleaseNotes = useCallback(() => {
		setConfluenceReleaseNotesDraftUrl(null);
		onClose();
	}, [onClose]);

	return (
		<ShortcutScope>
			{isAutomatedReleaseNotesEnabled && confluenceReleaseNotesDraftUrl ? (
				<LazyEmbeddedPageReleaseNotesEditorModal
					draftUrl={confluenceReleaseNotesDraftUrl}
					onClose={onCloseConfluenceReleaseNotes}
				/>
			) : (
				<ModalDialog width="x-large" autoFocus={false} onClose={props.onClose}>
					<Placeholder name="release-notes-modal" fallback={<LoadingModalBody />}>
						<LazyReleaseNotesModalContent
							{...props}
							onCreateConfluenceReleaseNotes={onCreateConfluenceReleaseNotes}
						/>
					</Placeholder>
				</ModalDialog>
			)}
		</ShortcutScope>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const LoadingWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 50}px`,
});
